import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, FileText, Zap, ArrowRight, Play, Star } from 'lucide-react';
import './styles.css';
import dashboardImg from './assets/DashboardCaseManagement.png';
import uploadImg from './assets/DocumentUploadProcess.png';
import deptImg from './assets/DepartmentSelection.png';
import codeImg from './assets/CodeSelectionbyState.png';
import reportImg from './assets/ComplianceReportGeneration.png';

const LandingPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);


  const showcaseSlides = [
    {
      title: "Smart Dashboard & Case Management",
      description: "Intuitive interface for building officials to manage permits and track project reviews. Select your case management tool and monitor active projects with real-time status updates.",
      features: ["Unified case management integration", "Role-based access control", "Real-time project tracking"],
      image: dashboardImg,
    },
    {
      title: "Simple Document Upload",
      description: "Upload architectural and engineering drawings with our streamlined interface. Choose between pre-check and compliance check reviews based on your project phase.",
      features: ["Drag-and-drop file upload", "Multiple file format support", "Project name and zone selection"],
      image: uploadImg,
    },
    {
      title: "Department-Specific Analysis",
      description: "Select the relevant departments for comprehensive review. Our AI analyzes documents according to each department's specific requirements and regulations.",
      features: ["Multi-department coordination", "Department-specific compliance rules", "Streamlined review workflow"],
      image: deptImg
    },
    {
      title: "Local Code Compliance",
      description: "Choose applicable building codes by state and jurisdiction. Our system stays updated with the latest local regulations and building standards.",
      features: ["State-specific building codes", "Local jurisdiction rules", "Automatic code updates"],
      image: codeImg
    },
    {
      title: "Detailed Compliance Reports",
      description: "Get comprehensive reports with visual markups showing compliance status. Download PDF reports with specific violation details and correction recommendations.",
      features: ["Visual redline markups", "Downloadable PDF reports", "Compliance score tracking"],
      image: reportImg
    }
  ];

  const features = [
    {
      icon: <FileText size={32} />,
      title: "Intelligent Document Analysis",
      description: "Extract critical specifications from architectural and engineering drawings automatically"
    },
    {
      icon: <CheckCircle size={32} />,
      title: "Real-Time Code Compliance",
      description: "Instantly verify designs against IBC, local codes, and industry standards"
    },
    {
      icon: <AlertTriangle size={32} />,
      title: "Smart Violation Detection",
      description: "Identify design issues before construction with specific correction suggestions"
    },
    {
      icon: <Zap size={32} />,
      title: "Lightning Fast Reviews",
      description: "Complete comprehensive plan reviews in minutes, not days"
    }
  ];

  const stats = [
    { number: "10x", label: "Faster Plan Reviews" },
    { number: "95%", label: "Violation Detection Rate" },
    { number: "$500K+", label: "Average Cost Savings" },
    { number: "50+", label: "Building Codes Supported" }
  ];

  const testimonials = [
    {
      quote: "This AI solution caught violations we missed during our manual review. Saved us from costly rework.",
      author: "Sarah Chen",
      role: "Principal Architect, BuildRight Studios",
      rating: 5
    },
    {
      quote: "Cut our permit approval time by 75%. The automated compliance reports are incredibly detailed.",
      author: "Mike Rodriguez",
      role: "Chief Building Official, Metro City",
      rating: 5
    }
  ];

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % showcaseSlides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [showcaseSlides.length]);


  return (
      <div className="container">
        {/* Navigation */}
        <nav className="nav">
          <div className="nav-content">
            <div className="logo">SwiftCivil</div>
            <div className="nav-links">
              <a href="#features">Features</a>
              <a href="#demo">Demo</a>
              <a href="#pricing">Pricing</a>
            </div>
            <button className="btn btn-primary">Get Started</button>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content">
            <div className={`hero-text ${isVisible ? 'visible' : 'hidden'}`}>
              <h1 className="hero-title">
                <span className="gradient-text">AI-Powered</span>
                <br />
                Code Compliance
              </h1>
              <p className="hero-subtitle">
                Catch design violations before construction begins. Our AI analyzes architectural and engineering documents,
                ensuring 100% code compliance with intelligent corrections.
              </p>
              <div className="button-group">
                <button className="btn btn-primary btn-large">
                  Start Free Trial
                  <ArrowRight size={20} />
                </button>
                <button className="btn btn-secondary btn-large">
                  <Play size={20} />
                  Watch Demo
                </button>
              </div>
            </div>
          </div>

          {/* Floating Elements */}
          <div className="floating-element floating-1"></div>
          <div className="floating-element floating-2"></div>
          <div className="floating-element floating-3"></div>
        </section>

        {/* Stats Section */}
        <section className="stats-section">
          <div className="stats-grid">
            {stats.map((stat, index) => (
                <div key={index} className="stat-item">
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
            ))}
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="section">
          <div className="section-content">
            <div className="section-header">
              <h2 className="section-title">
                Why Choose <span className="gradient-text">SwiftCivil</span>
              </h2>
              <p className="section-subtitle">
                Our advanced AI technology revolutionizes how you ensure code compliance in construction projects
              </p>
            </div>

            <div className="feature-grid">
              {features.map((feature, index) => (
                  <div key={index} className="feature-card">
                    <div className="feature-icon">{feature.icon}</div>
                    <h3 className="feature-title">{feature.title}</h3>
                    <p className="feature-description">{feature.description}</p>
                  </div>
              ))}
            </div>
          </div>
        </section>

        {/* App Showcase Section */}
        <section className="showcase-section">
          <div className="showcase-container">
            <div className="section-header">
              <h2 className="section-title">
                See <span className="gradient-text">SwiftCivil</span> in Action
              </h2>
              <p className="section-subtitle">
                Explore our intuitive interface and powerful features through this interactive walkthrough
              </p>
            </div>

            <div className="showcase-content">
              <div className="showcase-text">
                <div className="showcase-counter">
                  <span className="counter-current">{currentSlide + 1}</span>
                  <span>of {showcaseSlides.length}</span>
                </div>
                <h3 className="showcase-title">{showcaseSlides[currentSlide].title}</h3>
                <p className="showcase-description">{showcaseSlides[currentSlide].description}</p>
                <ul className="showcase-features">
                  {showcaseSlides[currentSlide].features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                  ))}
                </ul>
                <div className="showcase-navigation">
                  {showcaseSlides.map((_, index) => (
                      <div
                          key={index}
                          className={`nav-dot ${index === currentSlide ? 'active' : ''}`}
                          onClick={() => setCurrentSlide(index)}
                      />
                  ))}
                </div>
              </div>

              <div className="showcase-image">
                <img
                    src={showcaseSlides[currentSlide].image}
                    alt={showcaseSlides[currentSlide].title}
                    className="showcase-img"
                />
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="section section-dark">
          <div className="section-content">
            <div className="section-header">
              <h2 className="section-title">How It Works</h2>
              <p className="section-subtitle">Simple, powerful, and incredibly fast</p>
            </div>

            <div className="process-grid">
              <div className="process-step">
                <div className="process-number">1</div>
                <h3 className="process-title">Upload Documents</h3>
                <p className="process-description">Upload your architectural and engineering drawings in PDF format</p>
              </div>
              <div className="process-step">
                <div className="process-number">2</div>
                <h3 className="process-title">AI Analysis</h3>
                <p className="process-description">Our AI extracts specifications and cross-references with building codes</p>
              </div>
              <div className="process-step">
                <div className="process-number">3</div>
                <h3 className="process-title">Get Results</h3>
                <p className="process-description">Receive detailed compliance reports with specific correction suggestions</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="section">
          <div className="section-content">
            <div className="section-header">
              <h2 className="section-title">What Our Users Say</h2>
            </div>

            <div className="testimonial-grid">
              {testimonials.map((testimonial, index) => (
                  <div key={index} className="testimonial-card">
                    <div className="star-row">
                      {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} size={20} fill="#fbbf24" color="#fbbf24" />
                      ))}
                    </div>
                    <p className="testimonial-quote">"{testimonial.quote}"</p>
                    <div className="testimonial-author">{testimonial.author}</div>
                    <div className="testimonial-role">{testimonial.role}</div>
                  </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="cta-content">
            <h2 className="cta-title">
              Ready to Transform Your
              <br />
              <span className="gradient-text">Design Review Process?</span>
            </h2>
            <p className="cta-subtitle">
              Join hundreds of architects, engineers, and building officials who trust SwiftCivil
            </p>
            <div className="button-group">
              <button className="btn btn-primary btn-large">
                Start Free Trial - No Credit Card Required
              </button>
              <button className="btn btn-secondary btn-large">
                Schedule Demo
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="footer-content">
            <div className="footer-grid">
              <div className="footer-section">
                <div className="logo">SwiftCivil</div>
                <p style={{color: '#94a3b8', marginTop: '1rem'}}>
                  Revolutionizing construction code compliance with artificial intelligence.
                </p>
              </div>
              <div className="footer-section">
                <h4>Product</h4>
                <ul>
                  <li><a href="#">Features</a></li>
                  <li><a href="#">Pricing</a></li>
                  <li><a href="#">API</a></li>
                </ul>
              </div>
              <div className="footer-section">
                <h4>Company</h4>
                <ul>
                  <li><a href="#">About</a></li>
                  <li><a href="#">Careers</a></li>
                  <li><a href="#">Contact</a></li>
                </ul>
              </div>
              <div className="footer-section">
                <h4>Support</h4>
                <ul>
                  <li><a href="#">Documentation</a></li>
                  <li><a href="#">Help Center</a></li>
                  <li><a href="#">Status</a></li>
                </ul>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2025 SwiftCivil. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
  );
};

export default LandingPage;
